import { useState } from "react";
import { CaretUpDown, Check } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "./ui/command";

// Item card description used as the default line description (falls back to the part number).
export const itemLineDescription = (it) => (it?.description || "").trim() || it?.name || "";

// "Item No. — Description" label used wherever an item is picked.
export const itemLabel = (it) => {
  const desc = (it?.description || "").trim();
  return desc && desc !== it?.name ? `${it.name} — ${desc}` : it?.name || "";
};

export const isMobileViewport = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

// Searchable item picker showing item No. and description (search matches either).
export function ItemPicker({ value, items, onChange, testid, placeholder = "Select item", allowNone = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const current = items.find((x) => x.id === value);
  const pick = (id) => { setOpen(false); if (id !== value) onChange(id); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} title={current ? itemLabel(current) : ""}
          className={`flex h-8 w-full items-center justify-between gap-1 rounded-sm border border-input bg-background px-2 text-xs text-left ${className}`} data-testid={testid}>
          <span className={`truncate ${current ? "" : "text-muted-foreground"}`}>{current ? itemLabel(current) : placeholder}</span>
          <CaretUpDown size={12} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(28rem,calc(100vw-2rem))]" align="start" collisionPadding={8}>
        <Command filter={(v, search) => (v.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Search item No. or description…" className="h-9 text-xs" data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>No items found.</CommandEmpty>
            {allowNone && <CommandItem value="— none —" onSelect={() => pick("")} className="text-xs text-muted-foreground" data-testid={`${testid}-option-none`}>— None —</CommandItem>}
            {items.map((it) => (
              <CommandItem key={it.id} value={`${it.name} ${it.description || ""} ${it.id}`} onSelect={() => pick(it.id)} className="text-xs" data-testid={`${testid}-option-${it.id}`}>
                <Check size={12} className={`mr-1.5 shrink-0 ${it.id === value ? "opacity-100" : "opacity-0"}`} />
                <span className="font-mono font-semibold shrink-0">{it.name}</span>
                {(it.description || "").trim() && it.description.trim() !== it.name && <span className="ml-2 text-muted-foreground truncate">— {it.description.trim()}</span>}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Lets older lines (saved with only the part No.) pick up the Item Card description.
export function UseItemDescription({ item, description, onUse, testid = "line-use-item-desc" }) {
  const desc = (item?.description || "").trim();
  if (!desc || desc === (description || "").trim()) return null;
  return (
    <button type="button" onClick={() => onUse(desc)} className="self-start text-[11px] text-primary hover:underline" data-testid={testid}>
      Use item description: <span className="text-muted-foreground">{desc.length > 60 ? `${desc.slice(0, 60)}…` : desc}</span>
    </button>
  );
}

export function LinePartNo({ itemId, items, description, testid = "line-part-no" }) {
  const part = items.find((x) => x.id === itemId)?.name;
  if (!part || part === (description || "").trim()) return null;
  return <span className="font-mono text-[11px] text-muted-foreground" data-testid={testid}>{part}</span>;
}
